import React, { useState } from "react";
import { Link, NavLink, useNavigate, Outlet } from "react-router-dom";
import { Logo } from "@/components/brand/Logo";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutGrid, Store, Users, Calendar, BookOpen, MessageCircleHeart,
  Compass, Settings, LogOut, Menu, X, PenSquare, Inbox
} from "lucide-react";

const LINKS = [
  { to: "/dashboard", icon: LayoutGrid, label: "Dashboard", end: true },
  { to: "/dashboard/business", icon: Store, label: "My Business" },
  { to: "/dashboard/community", icon: Users, label: "Community" },
  { to: "/dashboard/events", icon: Calendar, label: "Events" },
  { to: "/dashboard/resources", icon: BookOpen, label: "Resources" },
  { to: "/dashboard/coaching", icon: MessageCircleHeart, label: "Coaching" },
  { to: "/dashboard/her-guide", icon: Compass, label: "HER Guide" },
  { to: "/dashboard/blog", icon: PenSquare, label: "Journal", adminOnly: true },
  { to: "/dashboard/leads", icon: Inbox, label: "Leads", adminOnly: true },
  { to: "/dashboard/settings", icon: Settings, label: "Settings" },
];

export default function DashboardShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const onLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  return (
    <div
      className="dashboard-scope min-h-screen flex"
      style={{ background: "var(--lh-surface)", color: "var(--lh-text)" }}
      data-testid="dashboard-shell"
    >
      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-40 w-72 transform ${open ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0 transition-transform duration-300 flex flex-col`}
        style={{
          background: "#FFFFFF",
          borderRight: "1px solid var(--lh-border)",
        }}
        data-testid="dashboard-sidebar"
      >
        <div className="px-7 py-7 flex items-center justify-between">
          <Link to="/" onClick={() => setOpen(false)}><Logo /></Link>
          <button className="lg:hidden p-1" onClick={() => setOpen(false)} aria-label="Close">
            <X size={20} />
          </button>
        </div>
        <nav className="flex-1 px-4 py-6 space-y-1">
          {LINKS.filter((l) => !l.adminOnly || user?.role === "admin").map((l) => {
            const Icon = l.icon;
            return (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 text-sm font-medium tracking-wide rounded-lg transition-all ${
                    isActive
                      ? "bg-[var(--lh-espresso-noir)] text-white shadow-sm"
                      : "text-[var(--lh-text)] hover:bg-[var(--lh-warm-linen)] hover:text-[var(--lh-cognac)]"
                  }`
                }
                data-testid={`sidebar-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <Icon size={17} strokeWidth={1.5} />
                <span className="uppercase text-[0.72rem] tracking-[0.18em]">{l.label}</span>
              </NavLink>
            );
          })}
        </nav>
        <div className="px-7 py-6" style={{ borderTop: "1px solid var(--lh-border)" }}>
          <div className="flex items-center gap-3 mb-4">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center font-serif-display text-lg"
              style={{
                background: "var(--lh-soft-blush)",
                color: "var(--lh-cognac)",
              }}
            >
              {(user?.name?.[0] || "M").toUpperCase()}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold truncate text-[var(--lh-text)]" data-testid="sidebar-user-name">{user?.name || "Member"}</div>
              <div className="text-xs text-[var(--lh-muted)] truncate">{user?.email}</div>
            </div>
          </div>
          <button
            onClick={onLogout}
            className="w-full text-left flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[var(--lh-muted)] hover:text-[var(--lh-cognac)] transition"
            data-testid="logout-button"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </aside>

      {/* Mobile overlay */}
      {open && (
        <button
          className="lg:hidden fixed inset-0 z-30 bg-black/30"
          onClick={() => setOpen(false)}
          aria-label="Close sidebar"
        />
      )}

      {/* Main */}
      <div className="flex-1 min-w-0 lg:pl-0">
        <div
          className="lg:hidden sticky top-0 z-20 flex items-center justify-between px-5 py-4"
          style={{
            background: "#FFFFFF",
            borderBottom: "1px solid var(--lh-border)",
          }}
        >
          <button onClick={() => setOpen(true)} aria-label="Open menu" data-testid="dashboard-mobile-menu-button">
            <Menu size={22} />
          </button>
          <Logo />
          <div className="w-6" />
        </div>
        <main className="px-6 md:px-10 lg:px-14 py-10 lg:py-14 max-w-[1300px] mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
